import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import {Paginator} from '../../../../tools/pagination/paginator';
import {TableSorter} from '../../../../tools/table-sorter/table-sorter';
import {UserEvent} from '../../../../models/user-event';
import {UserEventService} from '../../../../services/user-event/user-event.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import { NgClass, NgOptimizedImage } from '@angular/common';
import {PaginatorComponent} from '../../../layout/paginator/paginator.component';
import {CountryService} from '../../../../services/country/country.service';
import {FormsModule} from '@angular/forms';
import {DateRange} from '../../../../types/date-range'
import {TableSorterDirective} from '../../../../directives/table-sorter/table-sorter.directive';
import {TableSorterHeaderDirective} from '../../../../directives/table-sorter/table-sorter-header.directive';
import {User} from '../../../../models/user';
import {UserService} from '../../../../services/user/user.service';
import {IsGranted} from '../../../../decorator/is-granted';
import {MetaTitle} from '../../../../decorator/meta-title';
import {ListFilters} from '../../../../tools/list-filters/list-filters';
import {ListFilterService} from '../../../../services/list-filter/list-filter.service';
import {UserActivityService} from '../../../../services/user-activity/user-activity.service';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {ParisDatePipe} from '../../../../pipes/date/paris-date.pipe';
import {buildJourneys, JourneySession, JourneyStep} from '../../../../tools/user-journey/user-journey';

@Component({
  selector: 'app-activity-log',
  imports: [
    DateRangeSelectorComponent,
    ParisDatePipe,
    NgClass,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    PaginatorComponent,
    NgOptimizedImage,
    FormsModule,
    TableSorterDirective,
    TableSorterHeaderDirective
],
  templateUrl: './activity-log.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './activity-log.component.scss'
})
@IsGranted(['USER_EVENT_VIEW'])
@MetaTitle('User logs')
export class ActivityLogComponent implements OnInit {
  userEvents: UserEvent[] = [];
  journeyEvent: UserEvent | null = null;
  journey: JourneySession | null = null;
  journeyLoading = false;
  expandedStep: JourneyStep | null = null;
  paginator: Paginator = new Paginator();
  tableSorter: TableSorter = new TableSorter('createdAt', 'desc');
  dateRange!: DateRange;
  expandedUserEvent: UserEvent | null = null;
  users: User[] = [];
  types: { key: string, label: string, badge: string }[] = [
    {key: 'navigation', label: 'Navigation', badge: 'text-bg-light'},
    {key: 'filter', label: 'Filter', badge: 'text-bg-light'},
    {key: 'create', label: 'Creation', badge: 'text-bg-success'},
    {key: 'update', label: 'Update', badge: 'text-bg-primary'},
    {key: 'delete', label: 'Deletion', badge: 'text-bg-danger'},
    {key: 'download', label: 'Download', badge: 'text-bg-info'},
    {key: 'upload', label: 'Upload', badge: 'text-bg-warning'},
    {key: 'action', label: 'Action', badge: 'text-bg-secondary'},
    {key: 'login', label: 'Login', badge: 'text-bg-dark'},
    {key: 'logout', label: 'Logout', badge: 'text-bg-dark'},
    {key: 'denied', label: 'Denied', badge: 'text-bg-danger'},
  ];

  filters: ListFilters;

  private static readonly JOURNEY_MAX_EVENTS = 1000;
  private static readonly JOURNEY_WINDOW_MS = 12 * 60 * 60 * 1000;
  private static readonly STEP_ICONS: Record<string, string> = {
    login: 'bi-box-arrow-in-right',
    logout: 'bi-box-arrow-right',
    navigation: 'bi-file-earmark-text',
    filter: 'bi-funnel',
    download: 'bi-download',
    upload: 'bi-upload',
    create: 'bi-plus-circle',
    update: 'bi-pencil',
    delete: 'bi-trash',
    action: 'bi-lightning',
    denied: 'bi-shield-x',
  };

  constructor(
    private loader: LoaderService,
    private userEventService: UserEventService,
    private countryService: CountryService,
    private userService: UserService,
    private listFilterService: ListFilterService,
    private userActivity: UserActivityService,
  ) {
    this.filters = listFilterService.createFilters({
      userId: 'string',
      type: 'string[]',
      search: 'string',
    }, this.paginator, this.tableSorter, false);
  }

  ngOnInit(): void {
    this.loader.startLoading();
    this.userService.findAll({sort: 'firstname', order: 'asc'}).subscribe((users) => {
      this.users = users;

      this.loader.stopLoading();
    });

    // The first fetch is triggered by the initial dateChanged emission of the date-range-selector
    this.filters.init(() => {
      if (this.dateRange) {
        this.fetchAll();
      }
    });
  }

  refresh(): void {
    this.fetchAll();
  }

  fetchAll(): void {
    const params: any = {
      ...this.filters.toParams(),
      startDate: this.dateRange.start.toISOString(),
      endDate: this.dateRange.end.toISOString(),
    };

    this.userActivity.reportFilters(params);

    this.loader.startLoading();
    this.userEventService.findAll(params, this.paginator, this.tableSorter).subscribe((userEvents) => {
      this.userEvents = userEvents;

      this.loader.stopLoading();
    });
  }

  toggleJourney(userEvent: UserEvent): void {
    this.expandedStep = null;

    if (this.journeyEvent === userEvent) {
      this.journeyEvent = null;
      this.journey = null;
      return;
    }

    this.journeyEvent = userEvent;
    this.journey = null;
    this.fetchJourney(userEvent);
  }

  isExpandedJourney(userEvent: UserEvent): boolean {
    return this.journeyEvent === userEvent;
  }

  hasJourney(userEvent: UserEvent): boolean {
    return !!userEvent.user?.id && !!userEvent.createdAt;
  }

  isJourneyCurrentStep(step: JourneyStep): boolean {
    return !!this.journeyEvent && step.events.some(event => event.id === this.journeyEvent?.id);
  }

  toggleStep(step: JourneyStep): void {
    this.expandedStep = this.expandedStep === step ? null : step;
  }

  getStepIcon(step: JourneyStep): string {
    return ActivityLogComponent.STEP_ICONS[step.type] ?? 'bi-dot';
  }

  getStepEventsData(step: JourneyStep): string {
    return JSON.stringify(step.events.map(event => ({
      id: event.id,
      createdAt: event.createdAt,
      action: event.action,
      method: event.method,
      path: event.path,
      statusCode: event.statusCode,
      data: event.data,
    })), null, 2);
  }

  private fetchJourney(userEvent: UserEvent): void {
    const time = new Date(userEvent.createdAt!).getTime();
    const params = {
      userId: userEvent.user!.id,
      startDate: new Date(time - ActivityLogComponent.JOURNEY_WINDOW_MS).toISOString(),
      endDate: new Date(time + ActivityLogComponent.JOURNEY_WINDOW_MS).toISOString(),
    };
    const paginator = new Paginator(ActivityLogComponent.JOURNEY_MAX_EVENTS);

    this.journeyLoading = true;
    this.userEventService.findAll(params, paginator, new TableSorter('createdAt', 'desc')).subscribe({
      next: (userEvents) => {
        if (this.journeyEvent !== userEvent) {
          return;
        }

        this.journey = buildJourneys(userEvents).find(session =>
          session.steps.some(step => step.events.some(event => event.id === userEvent.id))
        ) ?? null;
        this.journeyLoading = false;
      },
      error: () => this.journeyLoading = false,
    });
  }

  getDuration(session: JourneySession): string {
    const minutes = Math.round((session.end.getTime() - session.start.getTime()) / 60000);

    return minutes < 60 ? minutes + ' min' : Math.floor(minutes / 60) + 'h' + String(minutes % 60).padStart(2, '0');
  }



  getFlagUrl(country: string): string {
    return this.countryService.getFlag(country);
  }

  dateChanged(dateRange: DateRange): void {
    this.dateRange = dateRange;
    this.refresh();
  }

  toggleData(userEvent: UserEvent): void {
    if (this.expandedUserEvent === userEvent) {
      this.expandedUserEvent = null;
    } else {
      this.expandedUserEvent = userEvent;
    }
  }

  hasData(userEvent: UserEvent): boolean {
    return !!userEvent.data || !!userEvent.userAgent;
  }

  getTypeLabel(userEvent: UserEvent): string {
    return this.types.find(type => type.key === userEvent.type)?.label ?? 'Other';
  }

  getTypeBadge(userEvent: UserEvent): string {
    return this.types.find(type => type.key === userEvent.type)?.badge ?? 'text-bg-light';
  }

  getStatusBadge(userEvent: UserEvent): string {
    if (!userEvent.statusCode) {
      return '';
    }

    return userEvent.statusCode >= 400 ? 'text-bg-danger' : 'text-bg-success';
  }

  isExpandedData(userEvent: UserEvent): boolean {
    return this.expandedUserEvent === userEvent;
  }

  getData(userEvent: UserEvent): string {
    return JSON.stringify({...userEvent.data, userAgent: userEvent.userAgent ?? undefined}, null, 2);
  }
}
